import React, { useState, useRef } from 'react';
import { StyleSheet, Text, View, TextInput, TouchableOpacity, Alert, ActivityIndicator, KeyboardAvoidingView, Platform, Image } from 'react-native';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AuthStackParamList } from '../../navigation/AuthNavigator';
import { appConfig } from '../../config/appConfig';

type Props = NativeStackScreenProps<AuthStackParamList, 'Login'>;

const LoginScreen = ({ navigation }: Props) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const passwordInputRef = useRef<TextInput | null>(null);

  const generateFriendCode = (): string => {
    return Math.random().toString(36).substring(2, 10).toUpperCase();
  };

  const createUserDocument = async (uid: string, email: string) => {
    try {
      // Verificar si el usuario ya existe en Firestore
      const userDoc = await getDoc(doc(firestore, 'users', uid));
      
      // Si el usuario no existe, crear un nuevo documento
      if (!userDoc.exists()) {
        const friendCode = generateFriendCode();
        
        // Crear un documento básico del usuario
        const userData = {
          id: uid,
          email,
          friendCode,
          friends: [],
          // Estos campos serán completados después en la configuración de perfil
          firstName: '',
          lastName: '',
          username: email.split('@')[0], // Uso provisional del email como username
        };
        
        await setDoc(doc(firestore, 'users', uid), userData);
        console.log('Documento de usuario creado para:', uid);
      }
    } catch (error) {
      console.error('Error al crear documento de usuario:', error);
      // No detener el flujo de login si esto falla
    }
  };

  const handleLogin = async () => {
    if (!email || !password) {
      setErrorMessage('Por favor ingresa tu correo y contraseña');
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;
      
      // Verificar y crear el documento de usuario si no existe
      await createUserDocument(uid, email);
      
      // If successful, navigation will handle automatically via the auth state listener
    } catch (error: any) {
      let message = 'Error desconocido al iniciar sesión';
      
      // Mapear códigos de error comunes de Firebase Auth
      if (error.code) {
        switch (error.code) {
          case 'auth/invalid-email':
            message = 'El formato del correo electrónico es inválido.';
            break;
          case 'auth/user-disabled':
            message = 'Esta cuenta ha sido deshabilitada.';
            break;
          case 'auth/user-not-found':
            message = 'No existe una cuenta con este correo electrónico.';
            break;
          case 'auth/wrong-password':
            message = 'La contraseña es incorrecta.';
            break;
          case 'auth/too-many-requests':
            message = 'Demasiados intentos fallidos. Intenta más tarde.';
            break;
          case 'auth/network-request-failed':
            message = 'Error de conexión. Verifica tu conexión a internet.';
            break;
          default:
            message = `Error de autenticación: ${error.message}`;
        }
      }
      
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent | any) => {
    // Verificar si la tecla presionada es Enter (código 13)
    if (e.nativeEvent && e.nativeEvent.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <KeyboardAvoidingView 
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      style={styles.container}
    >
      <View style={styles.logoContainer}>
        <Image 
          source={require('../../../assets/logo-1024.png')} 
          style={styles.logo} 
          resizeMode="contain"
        />
      </View>
      
      <Text style={styles.title}>{appConfig.name}</Text>
      <Text style={styles.subtitle}>Tu compañero de entrenamiento</Text>

      <View style={styles.inputContainer}>
        <TextInput
          style={styles.input}
          placeholder="Correo electrónico"
          value={email}
          onChangeText={(text) => {
            setEmail(text);
            setErrorMessage(null);
          }}
          keyboardType="email-address"
          autoCapitalize="none"
          returnKeyType="next"
          onSubmitEditing={() => { passwordInputRef.current?.focus(); }}
          blurOnSubmit={false}
          onKeyPress={handleKeyPress}
        />
        <TextInput
          style={styles.input}
          placeholder="Contraseña"
          value={password}
          onChangeText={(text) => {
            setPassword(text);
            setErrorMessage(null);
          }}
          secureTextEntry
          returnKeyType="go"
          onSubmitEditing={handleLogin}
          ref={passwordInputRef}
          onKeyPress={handleKeyPress}
        />
      </View>

      {errorMessage ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{errorMessage}</Text>
        </View>
      ) : null}

      <TouchableOpacity 
        style={styles.button} 
        onPress={handleLogin}
        disabled={loading}
        accessibilityRole="button"
      >
        {loading ? (
          <ActivityIndicator color="#fff" />
        ) : (
          <Text style={styles.buttonText}>Iniciar Sesión</Text>
        )}
      </TouchableOpacity>

      <TouchableOpacity 
        style={styles.linkButton}
        onPress={() => navigation.navigate('Register')}
      >
        <Text style={styles.linkText}>¿No tienes una cuenta? Regístrate</Text>
      </TouchableOpacity>

      <TouchableOpacity 
        style={styles.linkButton}
        onPress={() => navigation.navigate('PasswordRecovery')}
      >
        <Text style={styles.linkText}>¿Olvidaste tu contraseña?</Text>
      </TouchableOpacity>

      {/* Información de la app */}
      <View style={styles.appInfoContainer}>
        <Text style={styles.versionText}>
          {appConfig.name} v{appConfig.version}
        </Text>
        <View style={styles.legalLinksContainer}>
          <TouchableOpacity 
            onPress={() => navigation.navigate('TermsConditions')}
            style={styles.legalButton}
          >
            <Text style={styles.termsText}>Términos y Condiciones</Text>
          </TouchableOpacity>
          <Text style={styles.dividerText}>|</Text>
          <TouchableOpacity 
            onPress={() => navigation.navigate('PrivacyPolicy')}
            style={styles.legalButton}
          >
            <Text style={styles.termsText}>Política de Privacidad</Text>
          </TouchableOpacity>
        </View>
        <Text style={styles.copyrightText}>
          {appConfig.copyrightText}
        </Text>
      </View>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  logo: {
    width: 120,
    height: 120,
    borderRadius: 30,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 10,
    color: '#FF4500',
  },
  subtitle: {
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 40,
    color: '#555',
  },
  inputContainer: {
    marginBottom: 20,
  },
  input: {
    backgroundColor: 'white',
    padding: 15,
    borderRadius: 10,
    marginBottom: 15,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  button: {
    backgroundColor: '#FF4500',
    padding: 15,
    borderRadius: 10,
    alignItems: 'center',
  },
  buttonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 16,
  },
  linkButton: {
    marginTop: 15,
    alignItems: 'center',
  },
  linkText: {
    color: '#FF4500',
    fontSize: 14,
  },
  errorContainer: {
    marginBottom: 15,
    backgroundColor: '#FFEBEE',
    padding: 10,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#FFCDD2',
  },
  errorText: {
    color: '#D32F2F',
    textAlign: 'center',
    fontSize: 14,
  },
  appInfoContainer: {
    marginTop: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  versionText: {
    fontSize: 12,
    color: '#888',
    marginBottom: 5,
  },
  legalLinksContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 5,
  },
  legalButton: {
    padding: 5,
  },
  dividerText: {
    color: '#888',
    fontSize: 12,
    marginHorizontal: 5,
  },
  termsText: {
    fontSize: 12,
    color: '#FF4500',
    textDecorationLine: 'underline',
  },
  copyrightText: {
    fontSize: 10,
    color: '#888',
  },
});

export default LoginScreen; 